import type { QueryContainer } from "@opensearch-project/opensearch/api/_types/_common.query_dsl";

/** Catalog facet filters for Treez Products (OpenSearch). */
export type ProductCatalogFilters = {
    category?: string | null;
    brand?: string | null;
    /** `null` = any; `true` = in stock; `false` = out of stock. */
    isActive?: boolean | null;
};

/**
 * Text search clause for product name / title / brand fields (storefront-style).
 * Empty `query` → no clause (caller should use match_all or other base query).
 */
function buildSearchQueryFilter(
    query: string,
    fields: string[],
): QueryContainer | undefined {
    const q = query.trim();
    if (!q || fields.length === 0) {
        return undefined;
    }

    return {
        bool: {
            should: [
                {
                    multi_match: {
                        query: q,
                        fields,
                        type: "most_fields",
                    },
                },
                {
                    multi_match: {
                        query: q,
                        fields,
                        type: "phrase_prefix",
                    },
                },
            ],
            minimum_should_match: 1,
        },
    };
}

function buildProductCatalogFilterClauses(
    filters: ProductCatalogFilters = {},
): QueryContainer[] {
    const must: QueryContainer[] = [];

    const category = filters.category?.trim();
    if (category) {
        // match_phrase works on text fields; term often requires keyword mapping.
        must.push({
            bool: {
                should: [
                    { match_phrase: { category } },
                    { match_phrase: { category_name: category } },
                ],
                minimum_should_match: 1,
            },
        });
    }

    const brand = filters.brand?.trim();
    if (brand) {
        must.push({
            bool: {
                should: [
                    { match_phrase: { brand } },
                    { match_phrase: { brand_name: brand } },
                ],
                minimum_should_match: 1,
            },
        });
    }

    // Align with table badge (`isActive` mapped from isActive / in_stock / inStock).
    if (filters.isActive === true) {
        must.push({
            bool: {
                should: [
                    { term: { isActive: true } },
                    { term: { in_stock: true } },
                    { term: { inStock: true } },
                    { term: { available: true } },
                ],
                minimum_should_match: 1,
            },
        });
    } else if (filters.isActive === false) {
        must.push({
            bool: {
                should: [
                    { term: { isActive: false } },
                    { term: { in_stock: false } },
                    { term: { inStock: false } },
                    { term: { available: false } },
                ],
                minimum_should_match: 1,
            },
        });
    }

    return must;
}

/**
 * Root query for catalog search: name filter + optional facet filters, or match_all.
 */
function buildProductSearchRootQuery(
    searchText: string,
    nameSearchFields: string[],
    filters: ProductCatalogFilters = {},
): QueryContainer {
    const textQuery = buildSearchQueryFilter(searchText, nameSearchFields);
    const filterClauses = buildProductCatalogFilterClauses(filters);

    if (!textQuery && filterClauses.length === 0) {
        return { match_all: {} };
    }

    const must: QueryContainer[] = [];
    if (textQuery) {
        must.push(textQuery);
    }
    must.push(...filterClauses);

    if (must.length === 1) {
        return must[0];
    }

    return {
        bool: {
            must,
        },
    };
}

export {
    buildProductSearchRootQuery,
    buildSearchQueryFilter,
    buildProductCatalogFilterClauses,
};
